import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { useMemo } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import { MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { AtomList } from '@sorare/core/src/components/search/RefineList';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import { FilterWidget } from '@sorare/core/src/lib/filters';

import RefineListTitle from '@sorare/marketplace/src/search/RefineListTitle';

interface Team {
  fullName: string;
  svgUrl: string;
}

const Logo = styled.img`
  height: var(--double-and-a-half-unit);
  vertical-align: baseline;
  margin-right: var(--unit);
`;

const BeforeOption = ({ team }: { team?: Team }) => {
  if (!team) {
    return null;
  }
  return <Logo src={team.svgUrl} alt={team.fullName} />;
};

const RefineTeam = ({
  visible,
  attribute,
  teams,
  keepNoResults,
}: {
  attribute: string;
  teams: readonly Team[];
  keepNoResults: boolean;
  visible: boolean;
}) => {
  const track = useEvents();
  const {
    items,
    isFromSearch,
    refine: doRefine,
    searchForItems,
  } = useRefinementList({
    attribute,
    limit: 30,
  });
  const { indexUiState } = useInstantSearch();

  const refine = (value: string) => {
    track('Use Market Filter', {
      filterName: attribute,
      filterValue: value,
    });
    doRefine(value);
  };

  const actualItems = useMemo(() => {
    const uiRefinements = indexUiState.refinementList?.[attribute] || [];

    return teams
      .map(team => {
        const item = items.find(i => i.label === team.fullName);
        const count = item?.count || 0;

        // exclude facet value if searching and no hits
        if (isFromSearch && count === 0) {
          return null;
        }

        return {
          label: team.fullName,
          value: team.fullName,
          count,
          // we rather consider the refinement state from the UI
          // so that we don't rely on the Algolia answer to come back to reflect the checkbox state
          isRefined: !!uiRefinements.find(v => v === team.fullName),
        } as RefinementListItem;
      })
      .filter(Boolean)
      .filter(({ count }) => keepNoResults || !!count)
      .sort((a, b) => {
        // if we're searching within facet values, put first the highest count
        if (isFromSearch && a.count !== b.count) {
          return b.count - a.count;
        }
        // and default on alphabetical ordering
        return a.label.localeCompare(b.label);
      });
  }, [
    attribute,
    indexUiState.refinementList,
    isFromSearch,
    items,
    keepNoResults,
    teams,
  ]);

  return (
    <AtomList
      attribute={attribute}
      items={actualItems}
      refine={refine}
      searchForItems={searchForItems}
      renderBefore={option => (
        <BeforeOption team={teams.find(t => t.fullName === option)} />
      )}
      isFromSearch={isFromSearch}
      searchable
      visible={visible}
    />
  );
};

const buildRefineTeam = (
  filter: {
    attribute: string;
    title: MessageDescriptor;
  },
  teams: readonly Team[],
  keepNoResults = true
): FilterWidget => ({
  key: filter.attribute,
  type: 'list',
  attribute: filter.attribute,
  Component: ({ visible }) => (
    <RefineTeam
      attribute={filter.attribute}
      teams={teams}
      keepNoResults={keepNoResults}
      visible={visible}
    />
  ),
  title: <RefineListTitle attribute={filter.attribute} name={filter.title} />,
  accordionOptions: { startsOpen: false },
});

export default buildRefineTeam;
